// Libs
import clsx from "clsx";
import React from "react";
import { CardTitle, type CardTitleProps } from "src/design-system/molecules/cards/CardTitle";
import { Display, type DisplayProps } from "src/design-system/molecules/displays";
import { PillTabs, Tab } from "src/design-system/molecules/navigation";

export interface CardHeaderProps extends React.PropsWithChildren<unknown> {
    /**
     * Additional className
     */
    className?: string;
    /**
     * Additional className for the title
     */
    cardTitleClassName?: string;
    /**
     * Additional props for cardTitle
     */
    cardTitleProps: CardTitleProps;
    /**
     * Pill list with key
     */
    pillTabsList?: ({
        key?: string;
        disabled?: boolean;
        pillText?: string;
        title?: string | JSX.Element;
        chart?: JSX.Element;
        noDataMessage?: string;
        helperText?: React.ReactNode;
    } & DisplayProps)[];
    /**
     * Additional className for pillTabs component
     */
    pillTabsClassName?: string;
    /**
     * Additional className for tab
     */
    tabClassName?: string;
    /**
     * Additional props for pillTabs component
     */
    pillTabsProps?: {
        /**
         * onPillsChange
         */
        onPillsChange?: (value: string) => void;
        /**
         * Current active card key
         */
        pillTabValue?: string;
    };
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
    {
        className,
        cardTitleClassName: cardTitleClassNameProps,
        cardTitleProps,
        pillTabsList,
        pillTabsClassName: pillTabsClassNameProps,
        tabClassName: tabClassNameProps,
        pillTabsProps,
    },
    ref
) {
    const rootClassName = clsx("ds-c-card__header", className);
    const chartCardTitleClassName = clsx("ds-c-card__header__card_title", cardTitleClassNameProps);
    const pillTabsClassName = clsx("ds-c-card__header__pill_tabs", pillTabsClassNameProps);
    const tabClassName = clsx("ds-c-card__header__pill_tabs", tabClassNameProps);

    const { helperText, helperIcon, title } = cardTitleProps ?? {};
    const { onPillsChange, pillTabValue } = pillTabsProps ?? {};

    return (
        <div className={rootClassName} ref={ref}>
            <CardTitle
                extendedPadding
                helperText={helperText}
                helperIcon={helperIcon}
                className={chartCardTitleClassName}
            >
                {title}
            </CardTitle>
            {pillTabsList?.length > 1 && (
                <PillTabs
                    className={pillTabsClassName}
                    currentValue={pillTabValue}
                    onChange={(value) => {
                        onPillsChange(value.toString());
                    }}
                >
                    {pillTabsList.map((tab, index) => {
                        return (
                            <Tab
                                key={`tabs_${index}`}
                                value={tab.key}
                                className={tabClassName}
                                disabled={tab.disabled}
                                tooltip={tab.pillText}
                            >
                                <Display
                                    icon={tab.icon}
                                    value={tab.value}
                                    labelIconProps={tab.labelIconProps}
                                />
                            </Tab>
                        );
                    })}
                </PillTabs>
            )}
        </div>
    );
});
